import { ChevronLeft, RotateCw, TrashIcon } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import Search from "src/components/input/Search";
import { GenericPagination } from "src/components/ui/pagination";
import { StateList } from "src/utils/states";
import DisplayTableRow from "./DisplayTableRow";
import { Trainee } from "src/redux/types/_trainee.type";
import * as XLSX from 'xlsx';
import { useTrainees } from "src/redux/api/hooks/useTrainees";

function updateSearchParams(
  key: string,
  value: string,
  setSearchParams: (params: Record<string, string>) => void,
  searchParams: URLSearchParams,
) {
  const params: Record<string, string> = Object.fromEntries(searchParams);

  if (value) {
    params[key] = value;
  } else {
    delete params[key];
  }

  // any filter change should reset pagination back to page 1
  params.page = "1";

  setSearchParams(params);
}

interface TraineesListProps {
  editTrainee: (trainee: Trainee) => void;
}

const TraineesList = ({ editTrainee }: TraineesListProps) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const email = searchParams.get("email") || "";
  const name = searchParams.get("name") || "";
  const state = searchParams.get("state") || "";
  const district = searchParams.get("district") || "";
  // const time = searchParams.get("time") || "";
  // const post = searchParams.get("post") || "";

  const {
    trainees,
    isFetching: loading,
    isError,
    pageSize,
    currentPageNumber,
    hasNextPage,
    hasPrevPage,
    refetch
  } = useTrainees();

  // const lastNonEmptyPage = useRef(1); // safe default, not currentPageNumber

  // useEffect(() => {
  //   if (loading) return;
  //   if (trainees.length > 0) {
  //     lastNonEmptyPage.current = currentPageNumber;
  //     return;
  //   }

  //   // only correct course if we're not already on the target page
  //   if (
  //     currentPageNumber > 1 &&
  //     currentPageNumber !== lastNonEmptyPage.current
  //   ) {
  //     toast.error("No more Trainees");
  //     setSearchParams({
  //       ...Object.fromEntries(searchParams),
  //       page: lastNonEmptyPage.current.toString(),
  //     });
  //   }
  // }, [loading, trainees.length, currentPageNumber]);

  const handleNextPage = () => {
    if (!hasNextPage) {
      toast.error("No more Trainees");
      return;
    }
    setSearchParams({
      ...Object.fromEntries(searchParams),
      page: (currentPageNumber + 1).toString(),
    });
  };

  const handlePrevPage = () => {
    if (!hasPrevPage) return;
    setSearchParams({
      ...Object.fromEntries(searchParams),
      page: (currentPageNumber - 1).toString(),
    });
  };

  const handleJumpToPage = (page: number) => {
    if (page < 1) return;
    setSearchParams({
      ...Object.fromEntries(searchParams),
      page: page.toString(),
    });
  };

  const clearAllFilters = () => {
    setSearchParams({ page: "1", pageSize: pageSize.toString() });
  };

  const copyAddress = async (trainee: Trainee) => {
    const address =
      `Name: ${trainee.full_name?.toUpperCase()}\n` +
      `Phone: ${trainee.phone_no}\n` +
      `Address: ${trainee.building_name?.toUpperCase()}, ${trainee.landmark?.toUpperCase()}\n` +
      `City: ${trainee.city?.toUpperCase()}\n` +
      `District: ${trainee.district?.toUpperCase()}\n` +
      `State: ${trainee.state?.toUpperCase()}\n` +
      `PIN: ${trainee.pin}\n`
    navigator.clipboard
      .writeText(address)
      .then(() => {
        toast.success("Address copied to clipboard!");
      })
      .catch((err) => {
        console.error("Failed to copy: ", err);
        toast.error("Failed to copy details. Please try again.");
      });
  };

  // export trainee list to excel
  const exportTraineesToExcel = (
    trainees: Trainee[],
    fileName: string = "trainee-list"
  ) => {
    try {
      // Transform trainee data for better excel formatting
      const data = trainees.map((trainee) => ({
        "Full Name": trainee.full_name?.toUpperCase(),
        "Calling Number": trainee.phone_no,
        "Whatsapp Number": trainee.whatsapp_no,
        "Email": trainee.email,
        "State": trainee.state?.toUpperCase(),
        "District": trainee.district?.toUpperCase(),
        "Training Post": trainee.training_program?.toUpperCase(),
        "Preferred Time": trainee.preferred_time,
        "Transaction ID": trainee.transaction_id,
      }));

      // Create worksheet from data
      const worksheet = XLSX.utils.json_to_sheet(data);

      // Set column widths for better readability
      const columnWidths = [
        { wch: 10 }, // ID
        { wch: 25 }, // Full Name
        { wch: 15 }, // Phone Number
        { wch: 15 }, // Whatsapp Number
        { wch: 20 }, // Building Name
        { wch: 20 }, // Landmark
        { wch: 15 }, // City
        { wch: 15 }, // District
        { wch: 15 }, // State
        { wch: 10 }, // PIN Code
      ];
      worksheet["!cols"] = columnWidths;

      // Create workbook and add the worksheet
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Trainees");

      // Generate and download the Excel file
      XLSX.writeFile(workbook, `${fileName}.xlsx`);
    } catch (error) {
      console.error("Error exporting trainees to Excel:", error);
      throw new Error("Failed to export trainees to Excel");
    }
  };

  return (
    <div className="container mx-auto">
      <div className="rounded-lg shadow-md shadow-slate-300 py-4 px-2 sticky top-0 bg-white/40 backdrop-blur-md z-10">
        <div className="p-3 flex items-center justify-between">
          <h1 className="text-2xl font-semibold text-stone-600">
            Training Employee List
          </h1>
          <button
            className="text-sm flex gap-1 items-center border border-gray-600 bg-slate-100 hover:gray-200 px-2 py-1 rounded-lg shadow-md hover:shadow-gray-600"
            onClick={() => navigate("/admin")}
          >
            <ChevronLeft className="size-3.5" />
            Back
          </button>
        </div>
        <div className="flex gap-6 ">
          <div className="grid grid-cols-2 gap-4">
            <div className="grid grid-cols-2 gap-6">
              <Search
                value={email}
                placeholder="Search by Email"
                onChange={(value) =>
                  updateSearchParams(
                    "email",
                    value,
                    setSearchParams,
                    searchParams,
                  )
                }
              />
              <Search
                value={name}
                placeholder="Search by Name"
                onChange={(value) =>
                  updateSearchParams(
                    "name",
                    value,
                    setSearchParams,
                    searchParams,
                  )
                }
              />
            </div>
            <div className="grid grid-cols-4 gap-3">
              <select
                className="select select-bordered border-gray-400 w-full max-w-xs"
                value={state}
                onChange={(e) =>
                  updateSearchParams(
                    "state",
                    e.target.value,
                    setSearchParams,
                    searchParams,
                  )
                }
              >
                <option disabled selected value="">
                  Select State
                </option>
                {StateList.map((state) => (
                  <option key={state.state} value={state.state}>
                    {state.state}
                  </option>
                ))}
              </select>
              <select
                className="select select-md select-bordered border-gray-400 w-full max-w-xs"
                value={district}
                onChange={(e) =>
                  updateSearchParams(
                    "district",
                    e.target.value,
                    setSearchParams,
                    searchParams,
                  )
                }
              >
                <option disabled selected value="">
                  Select District
                </option>
                {StateList.flatMap((state) =>
                  state.districts.map((district) => (
                    <option key={district} value={district}>
                      {district}
                    </option>
                  )),
                )}
              </select>
              {/* <select
                className="select select-bordered border-amber-200 w-full max-w-xs"
                value={post}
                onChange={(e) =>
                  updateSearchParams(
                    "post",
                    e.target.value,
                    setSearchParams,
                    searchParams
                  )
                }
              >
                <option disabled selected value="">
                  Select Training
                </option>
                {["RWO Training", "DWO Training", "AWO Training"].map(
                  (training) => (
                    <option key={training} value={training}>
                      {training}
                    </option>
                  )
                )}
              </select> */}
              {/* <select
                className="select select-bordered border-amber-200 w-full max-w-xs"
                value={time}
                onChange={(e) =>
                  updateSearchParams(
                    "time",
                    e.target.value,
                    setSearchParams,
                    searchParams
                  )
                }
              >
                <option disabled selected value="">
                  Select Time
                </option>
                {[
                  "Morning: 08:30 am - 11:30 am",
                  "Noon: 12:00 pm - 05:00 pm",
                  "Evening: 06:00 pm - 11:00 pm",
                ].map((time) => (
                  <option key={time} value={time}>
                    {time}
                  </option>
                ))}
              </select> */}
            </div>
          </div>
          <div className="flex items-center justify-center">
            <button onClick={clearAllFilters}>
              <TrashIcon className="size-6" />
            </button>
          </div>
          <button
            className="flex items-center gap-2 border cursor-pointer px-2 py-1 rounded-md text-sm"
            onClick={() => refetch()}
          >
            <RotateCw className="size-3.5" />
            Refetch
          </button>
        </div>
      </div>

      <div>
        <div className="shadow-md shadow-slate-300 rounded-md mt-3">
          {isError && (
            <div>
              <p className="text-center text-red-500 font-semibold text-lg">
                Error fetching trainees. Please try again later.
              </p>
            </div>
          )}
          {loading ? (
            <div className="flex justify-center items-center h-full">
              <span className="loading loading-dots loading-lg size-20"></span>
            </div>
          ) : (
            <div className="rounded-2xl border border-base-300 bg-base-100 shadow-lg">
              <table className="table table-zebra">
                <thead className="bg-base-200">
                  <tr className="text-xs uppercase tracking-wider text-base-content/70">
                    <th className="py-4">Name</th>
                    <th>Calling Number</th>
                    <th>WhatsApp Number</th>
                    <th className="min-w-[320px]">Address</th>
                    <th>State / District</th>
                    <th>Training</th>
                    <th>Transaction ID</th>
                    <th className="text-center">Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {trainees &&
                    Array.isArray(trainees) &&
                    trainees.map((trainee) => (
                      <DisplayTableRow
                        key={trainee.id}
                        trainee={trainee}
                        editTrainee={editTrainee}
                        copyAddress={copyAddress}
                      />
                    ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="flex justify-between items-center p-3 border rounded-md rounded-t-none">
          <div className="flex items-center justify-between px-3 py-2 border rounded-md border-sky-300">
            <label className="font-semibold">Limit</label>
            <select
              value={pageSize}
              onChange={(e) => {
                setSearchParams({
                  ...Object.fromEntries(searchParams),
                  pageSize: e.target.value,
                });
              }}
              className="border p-1 rounded-md ml-2"
            >
              {[10, 25].map((value) => (
                <option value={value}>{value}</option>
              ))}
            </select>
          </div>
          <div className="flex-grow">
            <GenericPagination
              currentPage={currentPageNumber}
              onNext={handleNextPage}
              onPrev={handlePrevPage}
              jumpPage={handleJumpToPage}
            />
          </div>
          <div>
            <button
              className="btn btn-wide btn-outline border-gray-500"
              onClick={() => exportTraineesToExcel(trainees)}
            >
              Export List
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TraineesList;
